import React, { useEffect, useState, useRef } from "react";
import { Upload, FileSpreadsheet, RotateCcw, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { PageHeader, Card, Badge, Empty, RedButton, GhostButton, Field, inputCls } from "../../components/crm/ui";
import { toast } from "sonner";

export default function Imports() {
  const [imports, setImports] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [mapping, setMapping] = useState({});
  const [meta, setMeta] = useState({ source: "Import", import_name: "Import", campaign: "" });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/imports").then(({ data }) => setImports(data.items)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/imports/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAnalysis(data);
      setMapping(data.suggested_mapping);
      setMeta((m) => ({ ...m, import_name: file.name }));
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/imports/${analysis.import_id}/commit`, { mapping, metadata: meta });
      toast.success(`Imported: ${data.created} new, ${data.updated} updated, ${data.invalid} invalid, ${data.skipped} skipped`);
      setAnalysis(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const rollback = async (id) => {
    if (!window.confirm("Roll back this import? Contacts with no later activity will be archived.")) return;
    const { data } = await api.post(`/imports/${id}/rollback`);
    toast.success(`Archived ${data.archived}, preserved ${data.preserved}`); load();
  };

  return (
    <div>
      <PageHeader title="Data Import" subtitle="Upload CSV or XLSX. Map columns, validate, detect duplicates, then import safely." />

      {!analysis ? (
        <>
          <Card className="p-8 mb-6 border-dashed border-2 text-center">
            <FileSpreadsheet className="mx-auto text-slate-300 mb-4" size={40} />
            <p className="text-sm text-slate-600 mb-4">Upload a CSV or XLSX file of contacts.</p>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={onFile} className="hidden" data-testid="import-file" />
            <RedButton onClick={() => fileRef.current.click()} disabled={busy} data-testid="import-upload"><Upload size={15} /> {busy ? "Analyzing…" : "Choose File"}</RedButton>
          </Card>

          <h3 className="font-display font-bold text-slate-900 mb-3">Import History</h3>
          {imports.length === 0 ? <Card className="p-6"><Empty icon={Upload} title="No imports yet" /></Card> : (
            <Card className="divide-y divide-slate-100">
              {imports.map((i) => (
                <div key={i.id} className="p-4 flex items-center justify-between" data-testid={`import-row-${i.id}`}>
                  <div>
                    <p className="font-medium text-slate-800 text-sm">{i.filename}</p>
                    <p className="text-xs text-slate-500">{i.total_rows} rows · {i.created_count ?? 0} created · {i.updated_count ?? 0} updated · {i.invalid_count ?? 0} invalid</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge tone={i.status === "completed" ? "green" : i.status === "rolled_back" ? "amber" : "slate"}>{i.status}</Badge>
                    {i.status === "completed" && <GhostButton className="!py-1.5 !px-2.5" onClick={() => rollback(i.id)} data-testid={`rollback-${i.id}`}><RotateCcw size={14} /> Rollback</GhostButton>}
                  </div>
                </div>
              ))}
            </Card>
          )}
        </>
      ) : (
        <div className="space-y-6">
          <Card className="p-6">
            <h3 className="font-display font-bold text-slate-900 mb-1">Map Columns</h3>
            <p className="text-sm text-slate-500 mb-4">{analysis.total_rows} rows detected. Match your file's columns to CRM fields.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {analysis.headers.map((h) => (
                <div key={h} className="flex items-center gap-3" data-testid={`map-${h}`}>
                  <span className="text-sm font-medium text-slate-700 w-1/2 truncate">{h}</span>
                  <select className={inputCls} value={mapping[h] || "ignore"} onChange={(e) => setMapping({ ...mapping, [h]: e.target.value })}>
                    {analysis.crm_fields.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-display font-bold text-slate-900 mb-4">Global Metadata</h3>
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Import name"><input className={inputCls} value={meta.import_name} onChange={(e) => setMeta({ ...meta, import_name: e.target.value })} /></Field>
              <Field label="Source"><input className={inputCls} value={meta.source} onChange={(e) => setMeta({ ...meta, source: e.target.value })} data-testid="import-source" /></Field>
              <Field label="Campaign"><input className={inputCls} value={meta.campaign} onChange={(e) => setMeta({ ...meta, campaign: e.target.value })} /></Field>
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-display font-bold text-slate-900 mb-4">Preview (first 5 rows)</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs"><thead className="bg-slate-50 text-slate-500"><tr>{analysis.headers.map((h) => <th key={h} className="text-left px-3 py-2 font-semibold">{h}</th>)}</tr></thead>
                <tbody className="divide-y divide-slate-100">{analysis.preview.map((r, i) => <tr key={i}>{analysis.headers.map((h) => <td key={h} className="px-3 py-2 text-slate-600 truncate max-w-[160px]">{String(r[h] ?? "")}</td>)}</tr>)}</tbody>
              </table>
            </div>
          </Card>

          <div className="flex gap-3">
            <GhostButton onClick={() => setAnalysis(null)}>Cancel</GhostButton>
            <RedButton onClick={commit} disabled={busy} data-testid="import-commit"><CheckCircle2 size={15} /> {busy ? "Importing…" : "Confirm & Import"}</RedButton>
          </div>
        </div>
      )}
    </div>
  );
}
